import { createSlice } from "@reduxjs/toolkit";
import { api } from "../../app/api";

const initialState = {
  // The access token used to authenticate requests to the API
  accessToken: window.localStorage.getItem("token") || undefined,

  // The authenticated username
  uuid: window.localStorage.getItem("u") || undefined,

  // The authenticated username
  adminUuid: window.localStorage.getItem("a") || undefined,

  // When an ADMIN is impersonating another admin, these capture the original
  // admin's identity so the UI can render a "Return to admin" banner and
  // restore them cleanly. Both null/undefined when not impersonating.
  impersonatorUuid: window.localStorage.getItem("imp_actor_uuid") || undefined,
  impersonatorEmail: window.localStorage.getItem("imp_actor_email") || undefined,
};

export const selectAccessToken = (state) => state.auth.accessToken;
export const selectUuid = (state) => state.auth.uuid;
export const selectAdminUuid = (state) => state.auth.adminUuid;
export const selectImpersonatorUuid = (state) => state.auth.impersonatorUuid;
export const selectImpersonatorEmail = (state) => state.auth.impersonatorEmail;

// Save the access token to local storage
export function saveAccessToken(accessToken) {
  window.localStorage.setItem("token", accessToken);
}

// Save the access token and username to local storage
export function saveCredentials(accessToken, uuid)
{
  saveAccessToken(accessToken);
  window.localStorage.setItem("u", uuid);
}

// Remove the access token and username from local storage
function removeCredentials() {
  window.localStorage.removeItem("token");
  window.localStorage.removeItem("u");
  window.localStorage.removeItem("a");
  window.localStorage.removeItem("e");
  window.localStorage.removeItem("imp_actor_uuid");
  window.localStorage.removeItem("imp_actor_email");
}

export function saveAdminCredentials(accessToken, uuid, email)
{
  saveAccessToken(accessToken);
  window.localStorage.setItem("a", uuid);
  if (email) window.localStorage.setItem("e", email);
}

export function removeAdminCredentials() {
  window.localStorage.removeItem("token");
  window.localStorage.removeItem("a");
  window.localStorage.removeItem("e");
}

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      // This private reducer is not exported. The logout function below is the
      // public interface to log out, which also removes credentials from local
      // storage.
      state.accessToken = undefined;
      state.uuid = undefined;
      state.adminUuid = undefined;
      state.impersonatorUuid = undefined;
      state.impersonatorEmail = undefined;
    },
    impersonationStarted: (state, action) => {
      const { accessToken, targetUuid, targetEmail, actorUuid, actorEmail } = action.payload;
      state.accessToken = accessToken;
      state.adminUuid = targetUuid;
      state.impersonatorUuid = actorUuid;
      state.impersonatorEmail = actorEmail;
      // Keep the original admin's identity around so /impersonate/return can
      // restore it without needing another round-trip to identify them.
      window.localStorage.setItem("token", accessToken);
      window.localStorage.setItem("a", targetUuid);
      if (targetEmail) window.localStorage.setItem("e", targetEmail);
      window.localStorage.setItem("imp_actor_uuid", actorUuid);
      if (actorEmail) window.localStorage.setItem("imp_actor_email", actorEmail);
    },
    impersonationEnded: (state, action) => {
      const { accessToken, adminUuid, email } = action.payload;
      state.accessToken = accessToken;
      state.adminUuid = adminUuid;
      state.impersonatorUuid = undefined;
      state.impersonatorEmail = undefined;
      window.localStorage.setItem("token", accessToken);
      window.localStorage.setItem("a", adminUuid);
      if (email) window.localStorage.setItem("e", email);
      window.localStorage.removeItem("imp_actor_uuid");
      window.localStorage.removeItem("imp_actor_email");
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(api.endpoints.login.matchFulfilled, (state, action) => {
      state.accessToken = action.payload.access_token;
      state.uuid = action.payload?.uuid;
    });
    builder.addMatcher(api.endpoints.adminLogin.matchFulfilled, (state, action) => {
      state.accessToken = action.payload.access_token;
      state.adminUuid = action.payload?.uuid;
    });
    builder.addMatcher(api.endpoints.adminLoginMs.matchFulfilled, (state, action) => {
      state.accessToken = action.payload.access_token;
      state.adminUuid = action.payload?.uuid;
    });
  },
});

// Log out of the application.
//
// Call this instead of dispatching the logout() action directly, since this
// function also removes the token from the browser local storage.
export function logout() {
  return function logoutThunk(dispatch) {
    removeCredentials();
    dispatch(authSlice.actions.logout());
  };
}

export const { impersonationStarted, impersonationEnded } = authSlice.actions;

export default authSlice.reducer;
